
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";

import { useDispatch, useSelector } from "react-redux";

import Header from "../../../components/Header/Header";
import LeftSideBar from "../../../components/LeftSideBar/LeftSideBar";
import AdministratorHeader from "../../../components/Header/AdministratorHeader";
import Label from "../../../components/Label/Label";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";

import {
  clearApiStatus,
  fetchDrugTypes,
  createDrugType,
  saveDrugTypeApprovalSettings,
} from "../../../redux/application_configs/actions";

import "./ApprovalSettings.scss";

const ApprovalSettings = () => {
  const dispatch = useDispatch();

  const drugTypes = useSelector(
    (state) => state?.applicationConfigsReducer?.drugTypes ?? []
  );

  const apiStatus = useSelector(
    (state) => state?.applicationConfigsReducer?.apiStatus ?? {}
  );

  const [draftDrugTypes, setDraftDrugTypes] = useState([]);
  const [drugTypeName, setDrugTypeName] = useState("");
  const [mappingApproval, setMappingApproval] = useState(false);
  const [dataValidationApproval, setDataValidationApproval] = useState(false);
  const [search, setSearch] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("all");

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    dispatch(fetchDrugTypes());
  }, [dispatch]);

  // Sync API data with local draft data.
  useEffect(() => {
    setDraftDrugTypes(
      drugTypes.map((item) => ({
        ...item,
        pending_change_reason: "",
      }))
    );
  }, [drugTypes]);

  // Reset create form after successful creation.
  useEffect(() => {
    if (apiStatus.operation === "create" && apiStatus.apiSuccess) {
      setDrugTypeName("");
      setMappingApproval(false);
      setDataValidationApproval(false);
    }
  }, [apiStatus]);

  // Handle API notifications.
  useEffect(() => {
    if (
      !apiStatus.loader &&
      apiStatus.message &&
      (apiStatus.apiSuccess || apiStatus.apiError)
    ) {
      setNotification({
        open: true,
        message: apiStatus.message,
        severity: apiStatus.apiError ? "error" : "success",
      });
    }
  }, [apiStatus]);

  // Check whether an individual drug type has been modified.
  const isDrugTypeChanged = (draft) => {
    const saved = drugTypes.find((item) => item.id === draft.id);

    return (
      saved &&
      (draft.enable_mapping_review !== saved.enable_mapping_review ||
        draft.enable_validation_review !== saved.enable_validation_review)
    );
  };

  // Get all modified drug types.
  const changedDrugTypes = draftDrugTypes.filter(isDrugTypeChanged);

  // Check whether every modified drug type has a reason.
  const isChangeReasonValid = changedDrugTypes.every(
    (item) => item.pending_change_reason?.trim()
  );

  const filteredDrugTypes = draftDrugTypes.filter((item) => {
    const matchesName = item.name
      ?.toLowerCase()
      .includes(search.trim().toLowerCase());

    if (!matchesName) return false;

    if (approvalFilter === "mapping") {
      return item.enable_mapping_review;
    }

    if (approvalFilter === "data") {
      return item.enable_validation_review;
    }

    if (approvalFilter === "both") {
      return (
        item.enable_mapping_review && item.enable_validation_review
      );
    }

    if (approvalFilter === "none") {
      return (
        !item.enable_mapping_review && !item.enable_validation_review
      );
    }

    return true;
  });

  // Update approval switch.
  const handleApprovalChange = (id, field, checked) => {
    setDraftDrugTypes((previous) =>
      previous.map((item) =>
        item.id === id ? { ...item, [field]: checked } : item
      )
    );
  };

  // Update individual drug type change reason.
  const handleChangeReason = (id, value) => {
    setDraftDrugTypes((previous) =>
      previous.map((item) =>
        item.id === id
          ? { ...item, pending_change_reason: value }
          : item
      )
    );
  };

  // Create drug type.
  const handleCreateDrugType = (event) => {
    event.preventDefault();

    const name = drugTypeName.trim();

    if (!name) return;

    const duplicate = drugTypes.some(
      (item) => item.name.toLowerCase() === name.toLowerCase()
    );

    if (duplicate) {
      setNotification({
        open: true,
        message: "Drug type already exists.",
        severity: "warning",
      });
      return;
    }

    dispatch(
      createDrugType({
        name,
        enable_mapping_review: mappingApproval,
        enable_validation_review: dataValidationApproval,
      })
    );
  };

  // Save modified drug types with their individual change reasons.
  const handleSaveChanges = () => {
    if (
      !changedDrugTypes.length ||
      !isChangeReasonValid ||
      apiStatus.loader
    ) {
      return;
    }

    const drugTypesPayload = changedDrugTypes.map((item) => ({
      id: item.id,
      name: item.name,
      enable_mapping_review: item.enable_mapping_review,
      enable_validation_review: item.enable_validation_review,
      change_reason: item.pending_change_reason.trim(),
    }));

    dispatch(saveDrugTypeApprovalSettings(drugTypesPayload));
  };

  // Discard all unsaved changes.
  const handleDiscardChanges = () => {
    setDraftDrugTypes(
      drugTypes.map((item) => ({
        ...item,
        pending_change_reason: "",
      }))
    );
  };

  // Close notification.
  const handleCloseNotification = () => {
    setNotification((previous) => ({
      ...previous,
      open: false,
    }));

    dispatch(clearApiStatus());
  };

  const loading = apiStatus.loader && apiStatus.operation === "fetch";
  const creating = apiStatus.loader && apiStatus.operation === "create";
  const saving = apiStatus.loader && apiStatus.operation === "save";

  return (
    <Box className="approval-settings-page">
      <Header />

      <Grid container className="sidebar-main approval-settings-layout">
        <Grid item className="sidebar-container">
          <LeftSideBar page="sidebar" />
        </Grid>

        <Grid
          item
          className="visualization-conatiner approval-settings-content"
        >
          <AdministratorHeader text="Drug Types and Approval Settings" />

          <Box className="approval-settings-main">
            {/* Create Drug Type */}
            <Paper
              component="form"
              elevation={0}
              onSubmit={handleCreateDrugType}
              className="approval-create-card"
            >
              <Grid
                container
                spacing={1}
                alignItems="center"
                className="approval-create-row"
              >
                <Grid item xs={12} sm={4} md={2}>
                  <Box className="approval-create-title">
                    <Box className="approval-section-icon">
                      <AddRoundedIcon />
                    </Box>

                    <Label
                      text="Create Drug Type"
                      fontSize={14}
                      fontWeight={600}
                      color="#252a32"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={8} md={3.4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Drug Type Name"
                    placeholder="Enter drug type name"
                    value={drugTypeName}
                    onChange={(event) =>
                      setDrugTypeName(event.target.value)
                    }
                    inputProps={{ maxLength: 100 }}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.3}>
                  <Box className="approval-option-card">
                    <AccountTreeOutlinedIcon className="approval-option-icon" />

                    <Label
                      text="Mapping Approval"
                      fontSize={12}
                      fontWeight={500}
                      color="#333a45"
                    />

                    <Switch
                      size="small"
                      checked={mappingApproval}
                      onChange={(event) =>
                        setMappingApproval(event.target.checked)
                      }
                      inputProps={{
                        "aria-label": "Enable mapping approval",
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={2.3}>
                  <Box className="approval-option-card">
                    <FactCheckOutlinedIcon className="approval-option-icon" />

                    <Label
                      text="Data Validation"
                      fontSize={12}
                      fontWeight={500}
                      color="#333a45"
                    />

                    <Switch
                      size="small"
                      checked={dataValidationApproval}
                      onChange={(event) =>
                        setDataValidationApproval(event.target.checked)
                      }
                      inputProps={{
                        "aria-label": "Enable data validation",
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={creating || !drugTypeName.trim()}
                    startIcon={
                      creating ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <AddRoundedIcon />
                      )
                    }
                    className="approval-add-button"
                  >
                    {creating ? "Creating..." : "Add Drug Type"}
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Drug Types List */}
            <Paper elevation={0} className="approval-list-card">
              <Box className="approval-list-toolbar">
                <TextField
                  size="small"
                  placeholder="Search drug types..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="approval-search-field"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl
                  size="small"
                  className="approval-filter-field"
                >
                  <Select
                    value={approvalFilter}
                    onChange={(event) =>
                      setApprovalFilter(event.target.value)
                    }
                    inputProps={{
                      "aria-label": "Filter drug types by approval",
                    }}
                  >
                    <MenuItem value="all">All drug types</MenuItem>
                    <MenuItem value="mapping">
                      Mapping approval enabled
                    </MenuItem>
                    <MenuItem value="data">
                      Data validation enabled
                    </MenuItem>
                    <MenuItem value="both">Both enabled</MenuItem>
                    <MenuItem value="none">Both disabled</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TableContainer className="approval-table-container">
                <Table
                  stickyHeader
                  size="small"
                  aria-label="Drug types and approval settings"
                  className="approval-table-output"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="center"
                        className="approval-table-header"
                      >
                        Drug Type
                      </TableCell>

                      <TableCell className="approval-table-header">
                        Mapping Approval
                      </TableCell>

                      <TableCell className="approval-table-header">
                        Data Validation
                      </TableCell>

                      <TableCell className="approval-table-header">
                        Change Reason
                      </TableCell>

                      <TableCell className="approval-table-header">
                        Created By
                      </TableCell>

                      <TableCell className="approval-table-header">
                        Created On
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Box className="approval-table-message">
                            <CircularProgress size={22} />

                            <Label
                              text="Loading drug types..."
                              fontSize={13}
                              color="#657080"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : filteredDrugTypes.length ? (
                      filteredDrugTypes.map((item) => {
                        const isChanged = isDrugTypeChanged(item);

                        return (
                          <TableRow
                            hover
                            key={item.id}
                            className="approval-table-row"
                          >
                            <TableCell align="center">
                              <Box className="drug-type-name">
                                <Label
                                  text={item.name}
                                  fontSize={12}
                                  fontWeight={600}
                                  color="#252a32"
                                />
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Box className="approval-inline-toggle">
                                <Switch
                                  size="small"
                                  checked={item.enable_mapping_review}
                                  onChange={(event) =>
                                    handleApprovalChange(
                                      item.id,
                                      "enable_mapping_review",
                                      event.target.checked
                                    )
                                  }
                                  inputProps={{
                                    "aria-label": `Mapping approval for ${item.name}`,
                                  }}
                                />

                                <Label
                                  text={
                                    item.enable_mapping_review
                                      ? "Enabled"
                                      : "Disabled"
                                  }
                                  className={`approval-status-text ${
                                    item.enable_mapping_review ? "enabled" : ""
                                  }`}
                                  fontSize={11}
                                  color={
                                    item.enable_mapping_review
                                      ? "#236b42"
                                      : "#7a818b"
                                  }
                                />
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Box className="approval-inline-toggle">
                                <Switch
                                  size="small"
                                  checked={item.enable_validation_review}
                                  onChange={(event) =>
                                    handleApprovalChange(
                                      item.id,
                                      "enable_validation_review",
                                      event.target.checked
                                    )
                                  }
                                  inputProps={{
                                    "aria-label": `Data validation for ${item.name}`,
                                  }}
                                />

                                <Label
                                  text={
                                    item.enable_validation_review
                                      ? "Enabled"
                                      : "Disabled"
                                  }
                                  className={`approval-status-text ${
                                    item.enable_validation_review ? "enabled" : ""
                                  }`}
                                  fontSize={11}
                                  color={
                                    item.enable_validation_review
                                      ? "#236b42"
                                      : "#7a818b"
                                  }
                                />
                              </Box>
                            </TableCell>

                            {/* Individual Change Reason */}
                            <TableCell sx={{ minWidth: 240 }}>
                              {isChanged ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={1}
                                  maxRows={4}
                                  placeholder="Enter change reason"
                                  value={item.pending_change_reason || ""}
                                  onChange={(event) =>
                                    handleChangeReason(
                                      item.id,
                                      event.target.value
                                    )
                                  }
                                  error={
                                    !item.pending_change_reason?.trim()
                                  }
                                  helperText={
                                    !item.pending_change_reason?.trim()
                                      ? "Reason is required"
                                      : ""
                                  }
                                  inputProps={{ maxLength: 500 }}
                                />
                              ) : (
                                <Label
                                  text={item.change_reason || "—"}
                                  fontSize={12}
                                  color="#424955"
                                />
                              )}
                            </TableCell>

                            <TableCell>
                              <Label
                                text={item.created_by_username || "—"}
                                fontSize={12}
                                color="#424955"
                              />
                            </TableCell>

                            <TableCell>
                              <Label
                                text={item.created_date_time || "—"}
                                fontSize={12}
                                color="#424955"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Box className="approval-empty-state">
                            <LocalPharmacyOutlinedIcon />

                            <Label
                              text={
                                draftDrugTypes.length
                                  ? "No matching drug types"
                                  : "No drug types found"
                              }
                              className="approval-empty-title"
                              fontSize={13}
                              fontWeight={600}
                              color="#3d4653"
                            />

                            <Label
                              text={
                                draftDrugTypes.length
                                  ? "Try changing your search or approval filter."
                                  : "Create a drug type above to get started."
                              }
                              className="approval-empty-description"
                              fontSize={12}
                              color="#78818d"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Save and Discard Actions */}
              <Box className="approval-save-bar">
                <Label
                  text={`${changedDrugTypes.length} drug type${
                    changedDrugTypes.length === 1 ? "" : "s"
                  } modified`}
                  className="approval-unsaved-message"
                  fontSize={12}
                  fontWeight={600}
                  color="#5c6470"
                />

                <Box className="approval-save-actions">
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    startIcon={<RestartAltRoundedIcon />}
                    disabled={saving || changedDrugTypes.length === 0}
                    onClick={handleDiscardChanges}
                  >
                    Discard
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={
                      saving ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <SaveRoundedIcon />
                      )
                    }
                    disabled={
                      saving ||
                      changedDrugTypes.length === 0 ||
                      !isChangeReasonValid
                    }
                    onClick={handleSaveChanges}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Notification */}
      {notification.open && (
        <CustomizedSnackbars
          state={notification.open}
          severity={notification.severity}
          text={notification.message}
          handleClose={handleCloseNotification}
        />
      )}
    </Box>
  );
};

export default ApprovalSettings;
